import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Loader2, AlertCircle, Mail, Phone, MapPin, LogIn, ShieldAlert } from 'lucide-react';
import AdminLayout from '../components/AdminLayout';
import { getCandidateFullDetail, impersonateCandidate, CANDIDATE_APP_URL } from '../services/adminApi';
import { encodeImpersonationPayload } from '../../utils/impersonationToken';
import { resolveImageUrl } from '../../utils/resolveImageUrl';

const HEADER_KEYS = new Set(['name', 'surname', 'imageUrl']);
// Ham veride gelse de üst bilgi ızgarasında göstermek istemediğimiz alanlar
// (iç kimlik alanları, admin için anlamsız).
const HIDDEN_KEYS = new Set(['id', 'statusId', 'companyId', 'isAutoApplyEnabled']);
// Bu alanlar aday profilinin parçası olduğu için boş gelse bile "-" ile
// gösterilir, sessizce kaybolmazlar, admin neyin eksik olduğunu görür.
const ALWAYS_SHOW_KEYS = ['birthdate', 'city', 'district', 'location', 'currentLocation', 'militaryStatus'];
// createdAt/updatedAt gibi zaman damgalarında saat:dakika da gösterilir.
const TIME_KEYS = new Set(['createdAt', 'updatedAt']);

const LABELS = {
  email: 'E-posta',
  phone: 'Telefon',
  title: 'Unvan',
  birthdate: 'Doğum Tarihi',
  location: 'Lokasyon',
  currentLocation: 'Lokasyon',
  city: 'İl',
  district: 'İlçe',
  militaryStatus: 'Askerlik Durumu',
  gender: 'Cinsiyet',
  nationality: 'Uyruk',
  educationLevel: 'Eğitim Seviyesi',
  experienceYear: 'Deneyim (Yıl)',
  linkedinUrl: 'LinkedIn',
  cvText: 'CV Metni',
  createdAt: 'Kayıt Tarihi',
  updatedAt: 'Güncellenme Tarihi',
  source: 'Kaynak',
  companyName: 'Şirket',
  driverLicense: 'Ehliyet',
  summary: 'Hakkında',
  educations: 'Eğitim',
  experiences: 'Deneyim',
  applications: 'Başvurular',
  interviews: 'Mülakatlar',
  skills: 'Beceriler',
};

function humanizeKey(key) {
  if (LABELS[key]) return LABELS[key];
  return key.replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/^./, (c) => c.toUpperCase());
}

function isDateLike(value) {
  return typeof value === 'string' && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(value);
}

function formatValue(key, value) {
  if (value === null || value === undefined || value === '') return '-';
  if (typeof value === 'boolean') return value ? 'Evet' : 'Hayır';
  if (isDateLike(value)) {
    try {
      const opts = { day: 'numeric', month: 'long', year: 'numeric' };
      if (TIME_KEYS.has(key)) {
        opts.hour = '2-digit';
        opts.minute = '2-digit';
      }
      return new Date(value).toLocaleDateString('tr-TR', opts);
    } catch {
      return value;
    }
  }
  return String(value);
}

// Şema teyit edilmedi, hangi alanlar geleceği önceden bilinmiyor, o yüzden
// ham JSON göstermek yerine gelen HER alanı otomatik olarak okunur bir
// biçimde ekrana basıyoruz. Yeni bir alan BE'ye eklenirse burada da otomatik görünür.
function buildSections(raw) {
  const gridRows = [];
  const textBlocks = [];
  const listSections = [];
  const seen = new Set();

  Object.entries(raw).forEach(([key, value]) => {
    if (HEADER_KEYS.has(key) || HIDDEN_KEYS.has(key)) return;
    seen.add(key);

    if (value === null || value === undefined || value === '') {
      if (ALWAYS_SHOW_KEYS.includes(key)) gridRows.push({ key, value: null });
      return;
    }

    if (Array.isArray(value)) {
      if (value.length === 0) return;
      listSections.push({ key, items: value });
      return;
    }

    if (typeof value === 'object') {
      listSections.push({ key, items: [value] });
      return;
    }

    if (typeof value === 'string' && value.length > 140) {
      textBlocks.push({ key, value });
      return;
    }

    gridRows.push({ key, value });
  });

  // Ham veride hiç gelmeyen ama her zaman gösterilmesi istenen alanlar için
  // de bir satır ekle ("-" ile), admin eksik olduğunu görsün.
  ALWAYS_SHOW_KEYS.forEach((key) => {
    if (!seen.has(key)) gridRows.push({ key, value: null });
  });

  return { gridRows, textBlocks, listSections };
}

function itemFields(item) {
  return Object.entries(item)
    .filter(([, v]) => v !== null && v !== undefined && v !== '')
    .map(([k, v]) => ({
      key: k,
      label: humanizeKey(k),
      value: typeof v === 'object' ? JSON.stringify(v) : formatValue(k, v),
    }));
}

const AdminCandidateDetailPage = () => {
  const { id } = useParams();
  const [detail, setDetail] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [impersonating, setImpersonating] = useState(false);
  const [impersonateError, setImpersonateError] = useState('');

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError('');
      setDetail(null);
      try {
        const res = await getCandidateFullDetail(id);
        if (cancelled) return;

        if (!res.ok || res.status === false || !res.data) {
          setError(res.message || 'Aday detayı alınamadı.');
          return;
        }

        setDetail(res.data);
      } catch {
        if (!cancelled) setError('Sunucuya ulaşılamadı. Bağlantınızı kontrol edin.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [id]);

  const sections = detail ? buildSections(detail) : null;
  const fullName = detail ? [detail.name, detail.surname].filter(Boolean).join(' ') : '';

  const handleImpersonate = async () => {
    setImpersonating(true);
    setImpersonateError('');
    try {
      const res = await impersonateCandidate(id);

      if (!res.ok || res.status === false || !res.data?.token) {
        setImpersonateError(res.message || 'Giriş yapılamadı.');
        return;
      }

      const payload = encodeImpersonationPayload(res.data);
      window.open(`${CANDIDATE_APP_URL}/auth/impersonate#${payload}`, '_blank', 'noopener,noreferrer');
    } catch {
      setImpersonateError('Sunucuya ulaşılamadı. Bağlantınızı kontrol edin.');
    } finally {
      setImpersonating(false);
    }
  };

  return (
    <AdminLayout title="Aday Detayı">
      <Link to="/panel/adaylar" className="admin-back-link">
        <ArrowLeft size={16} />
        Adaylara Dön
      </Link>

      <div className="admin-panel-card">
        {loading && (
          <div className="admin-placeholder">
            <Loader2 className="spin" />
            <h3>Yükleniyor...</h3>
          </div>
        )}

        {!loading && error && (
          <div className="admin-placeholder">
            <AlertCircle />
            <h3>Aday detayı alınamadı</h3>
            <p>{error}</p>
          </div>
        )}

        {!loading && !error && detail && (
          <>
            <div className="admin-detail-header">
              {detail.imageUrl ? (
                <img src={resolveImageUrl(detail.imageUrl)} alt={fullName} className="admin-avatar admin-avatar--lg admin-avatar--photo" />
              ) : (
                <div className="admin-avatar admin-avatar--lg">{(detail.name || '?').trim().charAt(0).toUpperCase()}</div>
              )}
              <div className="admin-detail-header-info">
                <h2>{fullName || 'İsimsiz Aday'}</h2>
                {detail.title && <p className="admin-detail-subtitle">{detail.title}</p>}
              </div>
              <div className="admin-impersonate-block">
                <button
                  type="button"
                  className="admin-submit admin-submit--auto"
                  onClick={handleImpersonate}
                  disabled={impersonating}
                >
                  {impersonating ? <Loader2 size={16} className="spin" /> : <LogIn size={16} />}
                  {impersonating ? 'Giriş yapılıyor...' : 'Aday Olarak Giriş Yap'}
                </button>
                <p className="admin-impersonate-warning">
                  <ShieldAlert size={12} />
                  Bu adayın hesabına giriş yapacaksınız, tüm işlemler kayıt altına alınır.
                </p>
              </div>
            </div>

            {impersonateError && <div className="admin-error">{impersonateError}</div>}

            <div className="admin-detail-meta">
              {detail.email && <span><Mail size={14} /> {detail.email}</span>}
              {detail.phone && <span><Phone size={14} /> {detail.phone}</span>}
              {(detail.location || detail.currentLocation) && (
                <span><MapPin size={14} /> {detail.location || detail.currentLocation}</span>
              )}
            </div>

            {sections.gridRows.length > 0 && (
              <div className="admin-detail-section">
                <h4>Bilgiler</h4>
                <div className="admin-kv-grid">
                  {sections.gridRows.map(({ key, value }) => (
                    <div className="admin-kv-row" key={key}>
                      <span className="admin-kv-label">{humanizeKey(key)}</span>
                      <span className="admin-kv-value">{formatValue(key, value)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {sections.textBlocks.map(({ key, value }) => (
              <div className="admin-detail-section" key={key}>
                <h4>{humanizeKey(key)}</h4>
                <p className="admin-detail-text">{value}</p>
              </div>
            ))}

            {sections.listSections.map(({ key, items }) => (
              <div className="admin-detail-section" key={key}>
                <h4>{humanizeKey(key)}</h4>
                {items.map((item, i) => (
                  <div className="admin-kv-grid admin-kv-grid--card" key={i}>
                    {itemFields(item).map(({ key: k, label, value }) => (
                      <div className="admin-kv-row" key={k}>
                        <span className="admin-kv-label">{label}</span>
                        <span className="admin-kv-value">{value}</span>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            ))}
          </>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminCandidateDetailPage;
